import { es } from 'date-fns/locale';
import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../components/forms/CustomSelect';
import { DatePicker } from '../../../components/forms/DatePicker';
import RequiredInput from '../../../components/forms/InputHOC';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useUsers from '../../../hooks/api-calls/useUsers';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { WorkHourService } from '../../../services/workHour/workHourService';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import cn from '../../../utils/classNames';

interface WorkHoursFormProps {
    workHourSelected?: any;
    setOpenModal: any;
    formRef?: any;
    refetch: () => void;
}

const schema = yup.object().shape({
    employeeId: yup.string().required('El empleado es obligatorio'),
    startTime: yup.string().required('La hora de inicio es obligatoria'),
    endTime: yup.string().required('La hora de fin es obligatoria'),
    workDate: yup.date().required('La fecha de trabajo es obligatoria'),
});

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const WorkHoursForm: React.FC<WorkHoursFormProps> = ({ workHourSelected, setOpenModal, formRef, refetch }) => {

    // STATES
    const [data, setData] = useState(workHourSelected ? { ...workHourSelected } : {});
    const [loading, setLoading] = useState<boolean>(false);
    const [isSearched, setIsSearched] = useState<boolean>(false);

    // HOOKS
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getUsersList } = useUsers();

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES FUNCIÓN PARA ACTUALIZAR REGISTRO DE HORAS TRABAJADAS
     * @EN FUNCTION TO UPDATE WORK HOURS RECORD
     */
    // -----------------------------------------------------------------------------------------------------------
    const handleUpdate = async (values: any) => {
        try {
            setLoading(true);
            const workHourData = {
                id: data.id,
                employeeId: values.employeeId,
                startTime: values.startTime,
                endTime: values.endTime,  
                workDate: moment(values.workDate).format('YYYY-MM-DD'),
                company: values.company,
            };

            const response = (await (new WorkHourService()).editWorkHour(workHourData)).getResponseData();
            if (response?.success) {
                toast.success(response?.message ?? 'Registro de horas actualizado correctamente');
                refetch();
                setOpenModal(false);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setLoading(false);
        }
    };
    // -----------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES FUNCIÓN PARA CREAR REGISTRO DE HORAS TRABAJADAS
     * @EN FUNCTION TO CREATE WORK HOURS RECORD
     */
    // -----------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            setLoading(true);
            const workHourData = {
                employeeId: values.employeeId,
                startTime: values.startTime,
                endTime: values.endTime,
                workDate: moment(values.workDate).format('YYYY-MM-DD'),
                company: values.company,
            };

            const response = (await (new WorkHourService()).createWorkHour(workHourData)).getResponseData();
            if (response?.success) {
                toast.success(response?.message ?? 'Registro de horas creado correctamente');
                refetch();
                setOpenModal(false);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            handleErrors(error)
        } finally {
            setLoading(false);
        }
    };
    // -----------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES CONFIGURACIÓN DE FORMIK
     * @EN FORMIK CONFIGURATION
     */
    // -----------------------------------------------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            employeeId: data?.employee?.id ?? '',
            employeeCreatedAt: data?.employee?.createdAt ? moment(data.employee.createdAt.date).toDate() : null,
            startTime: data?.startTime ? moment(data.startTime?.date).format('HH:mm') : null,
            endTime: data?.endTime ? moment(data.endTime?.date).format('HH:mm') : null,
            workDate: data?.workDate ? moment(data.workDate.date).toDate() : null,
            company: data?.company?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (workHourSelected?.id) {
                handleUpdate(values);
            } else {
                handleCreate(values);
            }
        },
    });
    // -----------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------
    /**
     * @ES Efecto para prevenir que la fecha de trabajo sea anterior a la fecha de creación del empleado
     * @EN Effect to prevent work date from being before employee creation date
     */
    // -----------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (formik.values.employeeCreatedAt && formik.values.workDate && formik.values.workDate < formik.values.employeeCreatedAt) {
            formik.setFieldValue('workDate', formik.values.employeeCreatedAt);
        }
    }, [formik.values.employeeCreatedAt]);
    // -----------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);
    
    // RENDER
    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} className="space-y-6" ref={formRef ? formRef : undefined}>
                <div className="grid grid-column grid-cols-12 gap-6">
                    {/* Selector de Empleado */}
                    <CustomSelect
                        isSearchable
                        required
                        containerClassName="col-span-12"
                        id="employeeId"
                        label="Empleado"
                        placeholder="Selecciona un empleado"
                        options={getUsersList()}
                        value={getUsersList().find(user => user.value === formik.values.employeeId) || ''}
                        onChange={(selectedOption: any) => {
                            formik.setFieldValue('employeeId', selectedOption?.value || '');
                            formik.setFieldValue('employeeCreatedAt', selectedOption?.createdAt ? moment(selectedOption.createdAt).toDate() : null);
                        }}
                        error={formik.touched.employeeId && formik.errors.employeeId ? (typeof formik.errors.employeeId === 'string' ? formik.errors.employeeId : '') : ''}
                        display={userCan('list', 'user')}
                    />

                    {/* Hora de inicio */}
                    <RequiredInput
                        type="time"
                        id="startTime"
                        label="Hora de inicio"
                        value={formik.values.startTime}
                        onChange={(e: any) => {
                            const timeValue = e.target.value;
                            formik.setFieldValue('startTime', timeValue);
                        }}
                        onBlur={formik.handleBlur}
                        error={(formik.touched.startTime && formik.errors.startTime ? formik.errors.startTime : '')}
                        className="col-span-12"
                    />

                    {/* Hora de fin */}
                    <RequiredInput
                        type="time"
                        id="endTime"
                        label="Hora de fin"
                        value={formik.values.endTime}
                        onChange={(e: any) => {
                            const timeValue = e.target.value;
                            formik.setFieldValue('endTime', timeValue);
                        }}
                        onBlur={formik.handleBlur}
                        error={(formik.touched.endTime && formik.errors.endTime ? formik.errors.endTime : '')}
                        className="col-span-12"
                    />


                    {/* Fecha de trabajo */}
                    <div className="col-span-12">
                        <label htmlFor="workDate" className="block text-sm font-medium text-gray-700 mb-2">
                            Fecha de trabajo <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            monthsShown={1}
                            placeholderText="Selecciona una fecha"
                            inputProps={{
                                inputClassName: cn('h-9 [&_input]:text-ellipsis bg-white', formik.touched.workDate && formik.errors.workDate ? 'border-2 border-red-300' : ''),
                            }}
                            className="col-span-12"
                            selected={formik.values?.workDate ? moment(formik.values.workDate).toDate() : null}
                            onChange={(date: any) => {
                                formik.setFieldValue('workDate', date ? moment(date).format('YYYY-MM-DD') : '');
                            }}
                            locale={es}
                            minDate={formik.values.employeeCreatedAt ? moment(formik.values.employeeCreatedAt).toDate() : null}
                        />
                        {formik.touched.workDate && formik.errors.workDate && <p className="text-red-500 text-sm mt-2">{formik.errors.workDate}</p>}
                    </div>


                    {/* Add company selector if has permissions */}
                    {userCan('list', 'company') && (
                        <CustomSelect
                            isSearchable
                            required
                            containerClassName="col-span-12"
                            id="company"
                            label="Compañía"
                            placeholder="Selecciona una compañía"
                            options={getCompaniesList()}
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            onChange={(selectedOption: any) => {
                                formik.setFieldValue('company', selectedOption?.value || '');
                            }}
                            error={formik.touched.company && formik.errors.company ? (typeof formik.errors.company === 'string' ? formik.errors.company : '') : ''}
                        />
                    )}


                </div>

            </form>
        </div>
    );
};

export default WorkHoursForm;